import {
  takeLatest,
  put,
  call,
  CallEffect,
  PutEffect,
} from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { VerifyEmailPayload } from 'features/admin-email-verification/api/types';
import {
  sendEmailVerificationTokenAPI,
  verifyEmailAPI,
} from 'features/admin-email-verification/api';
import { emailVerificationActions } from 'features/admin-email-verification/redux/email-verification.actions';
import { toast } from 'react-toastify';
import { EMAIL_VERIFICATION_MESSAGES } from 'features/admin-email-verification/constants/email-verification-messages.const';
import { EmailVerificationStatus } from 'features/admin-email-verification/enums/email-verification-status.enum';

function* verifyEmail({
  payload,
}: PayloadAction<VerifyEmailPayload>): Generator<
  CallEffect<AxiosResponse<void>> | PutEffect,
  void,
  AxiosResponse<void>
> {
  try {
    yield call(verifyEmailAPI, payload);
    yield put(emailVerificationActions.verifyEmailSuccess());
  } catch (error) {
    if (error.response?.status === 429 || error.message === 'Network Error') {
      yield put(
        emailVerificationActions.verifyEmailFailure(
          EMAIL_VERIFICATION_MESSAGES.TOO_MANY_REQUESTS
        )
      );
      return;
    }

    switch (error.response?.data?.status) {
      case EmailVerificationStatus.Invalid:
        yield put(
          emailVerificationActions.verifyEmailFailure(
            EMAIL_VERIFICATION_MESSAGES.INVALID
          )
        );
        break;
      case EmailVerificationStatus.Expired:
        yield put(
          emailVerificationActions.verifyEmailFailure(
            EMAIL_VERIFICATION_MESSAGES.EXPIRED
          )
        );
        break;
      default:
        yield put(
          emailVerificationActions.verifyEmailFailure(
            EMAIL_VERIFICATION_MESSAGES.UNKNOWN
          )
        );
    }
  }
}

function* sendEmailVerificationToken(): Generator<
  CallEffect<AxiosResponse> | PutEffect,
  void,
  AxiosResponse
> {
  try {
    yield call(sendEmailVerificationTokenAPI);
    yield put(emailVerificationActions.sendEmailVerificationTokenSuccess());
    toast.success('Email verification sent successfully!');
  } catch (error) {
    if (error.response?.status === 429 || error.message === 'Network Error') {
      toast.error(EMAIL_VERIFICATION_MESSAGES.TOO_MANY_REQUESTS);
    }
    yield put(emailVerificationActions.sendEmailVerificationTokenFailure());
  }
}

function* emailVerificationSaga() {
  yield takeLatest(emailVerificationActions.verifyEmailRequest, verifyEmail);
  yield takeLatest(
    emailVerificationActions.sendEmailVerificationTokenRequest,
    sendEmailVerificationToken
  );
}

export default emailVerificationSaga;
